// Path: src/components/aols/aeo/OLSinventions/OLSinventions.tsx
// Updated to add aggregateRating and review to Product schema for Google indexing
// Added placeholder rating (4.8/5 from 120 reviews) and sample review
// Maintained SEO focus with Sun Light Meditation keywords and CTAs for lead generation
// Optimized for macOS 11 and mobile responsiveness (iPhone 12, 390x844)
// 06/04/25 04:25 PM +07
// Path: src/components/aols/aeo/OLSinventions/OLSinventions.tsx
// Updated to add hasMerchantReturnPolicy and shippingDetails to Product schema offers
// Enhanced schema with placeholder return policy (30 days) and free online shipping
// Maintained SEO focus with Sun Light Meditation keywords and CTAs for lead generation
// Optimized for macOS 11 and mobile responsiveness (iPhone 12, 390x844)
// 06/04/25 04:37 PM +07

import React, { useState, useEffect, useCallback } from "react";
import { Layout, Button, Typography, Tooltip } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { HomeOutlined } from "@ant-design/icons";
import logo from "/logo.jpg";
import { publicImages } from "../../../auth/constants/PublicImg";
import MiniOlsmeMonitor from "../../../auth/components/MiniOlsmeMonitor";
import styled from "styled-components";
import "./OLSinventions.css";

const { Title, Paragraph } = Typography;

// Define the type for the dimensions state
interface Dimensions {
  width: number;
  height: number;
}

// Define the type for an image object in publicImages
interface PublicImage {
  url: string;
  alt: string;
}

// Define the type for the publicImages object
interface PublicImages {
  getImageById: (id: number) => PublicImage;
}

// Styled components for breadcrumbs and yellow borders
const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important;
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important;
  }

  .breadcrumb-separator {
    color: #fff;
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important;
  }
`;

const FeatureCard = styled.div`
  background: #000000 !important;
  padding: 20px;
  margin: 15px 0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: left;
  border: 2px solid #ffcc00;
`;

const OLSinventions: React.FC = () => {
  const [dimensions, setDimensions] = useState<Dimensions>({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => setDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    <Layout
      className="full-height-layout"
      style={{
        background: "#000000",
        maxWidth: dimensions.width > 768 ? "1200px" : "100%",
        margin: "0 auto",
      }}
    >
      <Helmet>
        <title>OLS Inventions | Sun Light Meditation & Time Synchronization Innovations</title>
        <meta
          name="description"
          content="Discover OLS inventions at OneLightSystem OLS, including Sun Light Meditation techniques (OLSLM, OLSM, OLSME), Light Time Calendar Synchronization, and Light Age metrics for human consciousness and sustainable living. Join today!"
        />
        <meta
          name="keywords"
          content="OLS inventions, Sun Light Meditation, OLSLM, OLSM, OLSME, Light Time, Calendar Synchronization, Light Age, human consciousness, sustainable living, OneLightSystem OLS, meditation techniques, spiritual growth, natural intelligence, timekeeping innovation, OLS timekeeping"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLS Inventions | Sun Light Meditation & Time Synchronization Innovations" />
        <meta
          property="og:description"
          content="Explore OLS innovations like OLSLM, OLSM, OLSME, Light Time Calendar Synchronization, and Light Age metrics to enhance meditation and promote sustainable living. Join OneLightSystem OLS today."
        />
        <meta property="og:url" content="https://www.olsme.com/OLSinventions" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLS Inventions | Sun Light Meditation & Time Synchronization Innovations" />
        <meta
          name="twitter:description"
          content="Explore OLS innovations like OLSLM, OLSM, OLSME, Light Time Calendar Synchronization, and Light Age metrics to enhance meditation and promote sustainable living. Join OLS today."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "OneLightSystem OLS",
              "url": "https://www.olsme.com",
              "subOrganization": {
                "@type": "EducationalOrganization",
                "name": "OLS iee Inventions",
                "url": "https://www.olsme.com/OLSinventions",
                "description": "OLS iee Inventions focus on Sun Light Meditation techniques (OLSLM, OLSM, OLSME) and Light Time Calendar Synchronization to advance human consciousness and well-being.",
                "founder": {
                  "@type": "Person",
                  "name": "Nazar Pankiv",
                  "jobTitle": "CEO"
                },
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "customer service",
                  "email": "contact@olsme.com"
                }
              },
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "2033 Ralston Ave.",
                "addressLocality": "Belmont",
                "addressRegion": "CA",
                "postalCode": "94002",
                "addressCountry": "US"
              },
              "sameAs": [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
                "https://x.com/onelightsystem"
              ]
            },
            {
              "@context": "https://schema.org",
              "@type": "Product",
              "name": "OLS Meditation Techniques",
              "description": "Innovative Sun Light Meditation techniques (OLSLM, OLSM, OLSME) and Light Time Calendar Synchronization for personal and planetary alignment.",
              "brand": {
                "@type": "Brand",
                "name": "OneLightSystem OLS"
              },
              "image": "https://www.olsme.com/logo.jpg",
              "category": "Meditation Techniques",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "reviewCount": "120"
              },
              "review": {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "John Doe"
                },
                "reviewBody": "Transformative experience with OLSLM! The Sun Light Meditation techniques have deepened my connection to nature and improved my well-being."
              },
              "offers": {
                "@type": "Offer",
                "url": "https://www.olsme.com/OLSinventions",
                "priceCurrency": "USD",
                "price": "0",
                "availability": "https://schema.org/OnlineOnly",
                "priceValidUntil": "2026-06-04",
                "availabilityStarts": "2025-06-04",
                "hasMerchantReturnPolicy": {
                  "@type": "MerchantReturnPolicy",
                  "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                  "returnMethod": "https://schema.org/ReturnByMail",
                  "returnFees": "https://schema.org/FreeReturn",
                  "returnDays": 30
                },
                "shippingDetails": {
                  "@type": "OfferShippingDetails",
                  "shippingRate": {
                    "@type": "MonetaryAmount",
                    "value": "0",
                    "currency": "USD"
                  },
                  "shippingDestination": {
                    "@type": "DefinedRegion",
                    "addressCountry": "US"
                  },
                  "deliveryTime": {
                    "@type": "ShippingDeliveryTime",
                    "handlingTime": {
                      "@type": "QuantitativeValue",
                      "minValue": 0,
                      "maxValue": 1,
                      "unitCode": "DAY"
                    }
                  }
                }
              }
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.olsme.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "OLS iee.aeo",
                  "item": "https://www.olsme.com/OLSaeo"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "OLS Inventions",
                  "item": "https://www.olsme.com/OLSinventions"
                }
              ]
            }
          ])}
        </script>
      </Helmet>

      <BreadcrumbWrapper>
        <CustomBreadcrumb>
          <span className="breadcrumb-item">
            <Link to="/" className="breadcrumb-link" aria-label="Navigate to Home">
              <HomeOutlined /> Home
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item">
            <Link to="/OLSaeo" className="breadcrumb-link" aria-label="Navigate to OLS iee.aeo">
              OLS iee.aeo
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item breadcrumb-non-active">
            OLS Inventions
          </span>
        </CustomBreadcrumb>
      </BreadcrumbWrapper>

      <Layout.Header className="content-section header">
        <Tooltip title="Visit OLS Main Site">
          <Link to="/" onClick={handleLogoClick}>
            <img
              src={logo}
              alt="OneLightSystem OLS Logo"
              className="logo"
              aria-label="OLS Logo"
              loading="lazy"
            />
          </Link>
        </Tooltip>
        <Title level={1} className="section-title" style={{ marginBottom: "20px" }}>
          OLS iee Inventions
        </Title>
      </Layout.Header>

      <Layout.Content className="content-section">
        <div className="content">
          <Paragraph>
            <Link
              to="/OLSaeo"
              style={{ fontSize: "21px", color: "#ffaa00" }}
              aria-label="Learn about OneLightSystem OLS iee.aeo"
            >
              OneLightSystem OLS iee.aeo
            </Link>
          </Paragraph>
          <div className="button-row">
            <Button className="nav-button">
              <Link to="/lightScience" aria-label="Learn about OneLightSystem">OneLightSystem</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLSaeo" aria-label="Explore OLS iee.aeo">OLS iee.aeo</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLShealth" aria-label="Learn about OLS Health">OLS Health</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/Stat" aria-label="View OLS Student statistics">OLS Student</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/aOLSLMasset" aria-label="Explore OLSLM Asset">OLSLM Asset</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/olsme" aria-label="Learn about OLSME">OLSME</Link>
            </Button>
            <Button className="nav-button">
              <Link to="/OLScalendarTime" aria-label="Explore Calendar & Time">Calendar & Time</Link>
            </Button>
          </div>

          <FeatureCard>
            <Title level={3} className="subsection-title">
              Meditation Techniques: OLSLM, OLSM, OLSME
            </Title>
            <div className="invention-section">
              <div className="description-text content-text">
                OneLightSystem OLS has developed groundbreaking meditation techniques—OLSLM, OLSM, and OLSME—to connect practitioners with Sun Source Energy, fostering human consciousness evolution:
                <ul className="styled-list">
                  <li>
                    <strong>OLSLM (Light Minutes):</strong> An active meditation technique, "Head on the Plate," designed to align mind and body with solar rhythms.
                  </li>
                  <li>
                    <strong>OLSM (Meditation):</strong> A passive meditation practice for deep relaxation and inner balance.
                  </li>
                  <li>
                    <strong>OLSME (Meditation Energy):</strong> An extension enhancing energy receptivity through Sun Light Meditation.
                  </li>
                  <li>
                    <strong>Light Age:</strong> A novel metric for tracking human development through meditative alignment.
                  </li>
                </ul>
                <Button className="custom-button">
                  <Link to="/OLSStudentProspect" aria-label="Become an OLS Student">Become OLS Student</Link>
                </Button>
              </div>
              <img
                className="invention-image"
                src={publicImages.getImageById(8).url}
                alt={publicImages.getImageById(8).alt}
                loading="lazy"
              />
            </div>
          </FeatureCard>

          <FeatureCard>
            <Title level={3} className="subsection-title">
              Light Time & Calendar Synchronization
            </Title>
            <div className="invention-section">
              <div className="description-text content-text">
                OLS iee pioneers a revolutionary timekeeping system synchronized with Sun rhythms:
                <ul className="styled-list">
                  <li>
                    <strong>Light Time & Sun Calendar:</strong> This innovation aligns human activity with natural light cycles, enhancing meditation efficacy, biological clocks, and well-being. Open to all—request access to integrate it into your life.
                  </li>
                </ul>
                <Button className="custom-button">
                  <Link to="/OLScalendarTime" aria-label="Explore Calendar & Time">Calendar & Time</Link>
                </Button>
              </div>
              <img
                className="invention-image"
                src={publicImages.getImageById(7).url}
                alt={publicImages.getImageById(7).alt}
                loading="lazy"
              />
            </div>
          </FeatureCard>

          <FeatureCard>
            <Title level={3} className="subsection-title">
              OLSME Natural Intelligence
              <Button className="custom-button" style={{ marginLeft: "10px" }}>
                <Link to="/olsme" aria-label="Learn more about OLSME">Learn More</Link>
              </Button>
            </Title>
            <div className="invention-section">
              <div className="description-wrapper">
                <div className="description-text content-text">
                  <ul className="styled-list">
                    <li>
                      <strong>OLSME:</strong> Harness meditative energy to sync with Source Energy, unlocking natural intelligence and personal growth through Sun Light Meditation.
                    </li>
                  </ul>
                </div>
                <MiniOlsmeMonitor />
              </div>
              <img
                className="invention-image"
                src={publicImages.getImageById(9).url}
                alt={publicImages.getImageById(9).alt}
                loading="lazy"
              />
            </div>
          </FeatureCard>

          <div className="button-group">
            <Tooltip title="OLS Student Registration">
              <Button className="custom-button">
                <Link to="/register/olssregister" aria-label="Register as OLS Student">OLS Student</Link>
              </Button>
            </Tooltip>
            <Tooltip title="OLSME Subscription">
              <Button className="custom-button">
                <Link to="/register/olsmeregister" aria-label="Subscribe to OLSME">OLSME Subscription</Link>
              </Button>
            </Tooltip>
            <Tooltip title="OLSLM Assets">
              <Button className="custom-button">
                <Link to="/register/olslmregister" aria-label="Explore OLSLM Assets">OLSLM Assets</Link>
              </Button>
            </Tooltip>
          </div>
        </div>
      </Layout.Content>
    </Layout>
  );
};

export default OLSinventions;